import React, { useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import JsBarcode from 'jsbarcode';
import { QRCodeSVG } from 'qrcode.react';
import { renderToStaticMarkup } from 'react-dom/server';
import DOMPurify from 'dompurify';

/**
 * Printable baggage tag.
 *
 * Two ways to print:
 *  1. The page-portal flow (legacy): mounts <BaggageTagPrint /> as a portal
 *     onto document.body and calls `triggerPrint()`. This relies on
 *     @media print CSS to hide the rest of the app.
 *  2. The popup-iframe flow (preferred): call `printBaggageTagInPopup(...)`.
 *     It opens a hidden iframe in a fresh document, injects the tag HTML +
 *     scoped CSS, and prints that iframe. The parent app's overflow / sticky
 *     / portal quirks are completely bypassed — what you see is what gets
 *     printed. This is what we use from every "Print" button in the app.
 */
const maskMobile = (m = '') => {
  if (!m) return '—';
  const digits = String(m).replace(/\D/g, '').slice(-10);
  if (digits.length < 10) return digits || '—';
  return `XXXXXX${digits.slice(-4)}`;
};

const Barcode = ({ value }) => {
  const ref = useRef(null);
  useEffect(() => {
    if (ref.current && value) {
      try {
        JsBarcode(ref.current, value, {
          format: 'CODE128', width: 3, height: 90,
          displayValue: false, margin: 4,
          background: '#FFFFFF', lineColor: '#000000'
        });
      } catch (e) {
        console.error('Barcode generation failed:', e);
      }
    }
  }, [value]);
  return <svg ref={ref} data-testid="baggage-barcode-svg" />;
};

// Inline CSS embedded in the print popup — kept here so the popup is fully
// self-contained and doesn't depend on the parent app's stylesheet.
const PRINT_CSS = `
  @page { size: A4; margin: 10mm; }
  * { box-sizing: border-box; }
  body {
    font-family: 'IBM Plex Mono', 'Courier New', monospace;
    margin: 0; padding: 0; color: #000; background: #fff;
    -webkit-print-color-adjust: exact;
    print-color-adjust: exact;
  }
  .bag-tag-page { page-break-after: always; padding: 0; }
  .bag-tag-page:last-child { page-break-after: auto; }
  .bag-tag-card {
    border: 4px solid #000; padding: 24px 28px; background: #fff;
    color: #000; font-size: 20px; line-height: 1.35;
  }
  .tag-header { text-align: center; padding-bottom: 14px; border-bottom: 3px dashed #000; margin-bottom: 18px; }
  .tag-brand-row { display: flex; align-items: center; justify-content: center; gap: 14px; }
  .tag-logo { width: 56px; height: 56px; object-fit: contain; border-radius: 6px; }
  .tag-brand { font-weight: 900; font-size: 42px; letter-spacing: 0.05em; }
  .tag-sub { font-size: 16px; letter-spacing: 0.2em; margin-top: 8px; color: #222; font-weight: 700; }
  .tag-route { display: flex; align-items: stretch; justify-content: space-between; gap: 18px; margin-bottom: 18px; }
  .tag-route-side { flex: 1; }
  .tag-route-label { font-size: 16px; letter-spacing: 0.2em; color: #444; font-weight: 700; }
  .tag-route-city { font-size: 44px; font-weight: 900; letter-spacing: 0.03em; line-height: 1.05; margin-top: 4px; }
  .tag-route-addr { font-size: 17px; color: #111; margin-top: 6px; word-break: break-word; font-weight: 500; }
  .tag-arrow { font-size: 56px; font-weight: 900; align-self: center; }
  .tag-customer, .tag-bag-details { display: grid; grid-template-columns: 1fr 1fr; gap: 16px; padding: 14px 0; border-top: 2px solid #000; }
  .tag-bag-grid { display: grid; grid-template-columns: 1fr 1fr 1fr 1fr; gap: 14px; width: 100%; }
  .tag-mini-label { font-size: 14px; letter-spacing: 0.18em; color: #444; font-weight: 700; }
  .tag-mini-value { font-size: 22px; font-weight: 800; word-break: break-word; margin-top: 4px; line-height: 1.1; }
  .tag-codes { display: flex; align-items: center; justify-content: space-between; gap: 22px; border-top: 2px solid #000; padding-top: 14px; margin-top: 14px; }
  .tag-barcode { flex: 1; text-align: center; }
  .tag-barcode svg { width: 100%; max-width: 380px; height: auto; }
  .tag-number { font-size: 28px; font-weight: 900; letter-spacing: 0.14em; margin-top: 6px; }
  .tag-qr { text-align: center; }
  .tag-qr-label { font-size: 14px; letter-spacing: 0.18em; color: #222; margin-top: 6px; font-weight: 700; }
  .tag-footer { border-top: 2px solid #000; margin-top: 14px; padding-top: 10px; font-size: 14px; letter-spacing: 0.12em; color: #111; text-align: center; font-weight: 700; }
`;

const SingleTagMarkup = ({ booking, customerName, mobile, bag, bagIndex, totalBags, logoUrl, trackUrl }) => {
  const tagValue = totalBags > 1 ? `${booking.baggage_tag}-${bagIndex}` : booking.baggage_tag;
  return (
    <div className="bag-tag-page">
      <div className="bag-tag-card">
        <div className="tag-header">
          <div className="tag-brand-row">
            <img src={logoUrl} alt="" className="tag-logo" />
            <div className="tag-brand">LUGGAGEFREEJOURNEY</div>
          </div>
          <div className="tag-sub">OFFICIAL BAGGAGE TAG · BAG {bagIndex} OF {totalBags}</div>
        </div>
        <div className="tag-route">
          <div className="tag-route-side">
            <div className="tag-route-label">FROM</div>
            <div className="tag-route-city">{(booking.pickup_location || '').toUpperCase()}</div>
            <div className="tag-route-addr">{booking.pickup_address || ''}</div>
          </div>
          <div className="tag-arrow">→</div>
          <div className="tag-route-side">
            <div className="tag-route-label">TO</div>
            <div className="tag-route-city">{(booking.delivery_location || '').toUpperCase()}</div>
            <div className="tag-route-addr">{booking.delivery_address || ''}</div>
          </div>
        </div>
        <div className="tag-customer">
          <div>
            <div className="tag-mini-label">PASSENGER</div>
            <div className="tag-mini-value">{customerName.toUpperCase()}</div>
          </div>
          <div>
            <div className="tag-mini-label">MOBILE</div>
            <div className="tag-mini-value">{maskMobile(mobile)}</div>
          </div>
        </div>
        <div className="tag-bag-details">
          <div className="tag-bag-grid">
            <div>
              <div className="tag-mini-label">TYPE</div>
              <div className="tag-mini-value">{(bag.bag_type || 'BAG').toUpperCase()}</div>
            </div>
            <div>
              <div className="tag-mini-label">COLOR</div>
              <div className="tag-mini-value">{(bag.color || '—').toUpperCase()}</div>
            </div>
            <div>
              <div className="tag-mini-label">COMPANY/BRAND</div>
              <div className="tag-mini-value">{(bag.brand || '—').toUpperCase()}</div>
            </div>
            <div>
              <div className="tag-mini-label">WEIGHT</div>
              <div className="tag-mini-value">{bag.weight ? `${bag.weight} KG` : '—'}</div>
            </div>
          </div>
        </div>
        <div className="tag-codes">
          <div className="tag-barcode">
            <BarcodeStatic value={tagValue} />
            <div className="tag-number">{tagValue}</div>
          </div>
          <div className="tag-qr">
            <QRCodeSVG value={trackUrl} size={170} level="M" includeMargin={false} />
            <div className="tag-qr-label">SCAN TO TRACK</div>
          </div>
        </div>
        <div className="tag-footer">
          IF FOUND, CONTACT LUGGAGEFREEJOURNEY · KEEP THIS TAG SAFE
        </div>
      </div>
    </div>
  );
};

// SSR-safe barcode rendered as an inline SVG string by JsBarcode.
const BarcodeStatic = ({ value }) => {
  // Generate the SVG markup once at render time. Use a detached SVG element.
  let svgString = '';
  try {
    if (typeof document !== 'undefined' && value) {
      const tmp = document.createElement('div');
      const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
      tmp.appendChild(svg);
      JsBarcode(svg, value, {
        format: 'CODE128', width: 3, height: 130,
        displayValue: false, margin: 4,
        background: '#FFFFFF', lineColor: '#000000',
        xmlDocument: document
      });
      svgString = tmp.innerHTML;
    }
  } catch (e) {
    console.error('BarcodeStatic generation failed:', e);
  }
  // Sanitize the JsBarcode-generated SVG before injecting. JsBarcode only
  // emits trusted SVG primitives, but DOMPurify is defense-in-depth in case
  // upstream regressions ever introduce script/foreign content.
  const safeSvg = svgString
    ? DOMPurify.sanitize(svgString, { USE_PROFILES: { svg: true, svgFilters: true } })
    : '';
  return <span dangerouslySetInnerHTML={{ __html: safeSvg }} />;
};

/**
 * Print a baggage tag in a hidden iframe. Always works regardless of the
 * parent app's CSS / portals / overflow guards.
 */
export const printBaggageTagInPopup = (booking, customer) => {
  if (!booking) return;
  const bags = booking.luggage_details?.bags || [{}];
  const customerName = customer?.full_name || customer?.name || booking.customer_name || '—';
  const aadhaar = customer?.aadhaar_number || customer?.aadhaar_or_phone || booking.aadhaar_or_phone || '';
  const mobile = customer?.mobile_number || customer?.aadhaar_or_phone || booking.customer_phone || aadhaar;
  const logoUrl = `${window.location.origin}/lfj-logo.png`;
  const trackUrl = `${window.location.origin}/track?tag=${booking.baggage_tag}`;

  const body = bags.map((bag, idx) =>
    renderToStaticMarkup(
      <SingleTagMarkup
        booking={booking}
        customerName={customerName}
        mobile={mobile}
        bag={bag}
        bagIndex={idx + 1}
        totalBags={bags.length}
        logoUrl={logoUrl}
        trackUrl={trackUrl}
      />
    )
  ).join('');

  const html = `<!doctype html>
<html lang="en">
<head>
  <meta charset="utf-8" />
  <title>Baggage Tag · ${booking.baggage_tag}</title>
  <style>${PRINT_CSS}</style>
</head>
<body>
  ${body}
  <script>
    (function () {
      // Wait for all images (logo) to settle then print.
      var imgs = Array.prototype.slice.call(document.images || []);
      var pending = imgs.filter(function (i) { return !i.complete; });
      if (pending.length === 0) { window.focus(); window.print(); return; }
      var remaining = pending.length;
      function done () { remaining -= 1; if (remaining <= 0) { window.focus(); window.print(); } }
      pending.forEach(function (i) {
        i.addEventListener('load', done, { once: true });
        i.addEventListener('error', done, { once: true });
      });
      setTimeout(function () { window.focus(); window.print(); }, 1500);
    })();
  </script>
</body>
</html>`;

  // Use a hidden iframe (no popup-blocker problems, no white flash).
  let iframe = document.getElementById('lfj-print-iframe');
  if (!iframe) {
    iframe = document.createElement('iframe');
    iframe.id = 'lfj-print-iframe';
    iframe.style.position = 'fixed';
    iframe.style.right = '0';
    iframe.style.bottom = '0';
    iframe.style.width = '0';
    iframe.style.height = '0';
    iframe.style.border = '0';
    document.body.appendChild(iframe);
  }
  const doc = iframe.contentDocument || iframe.contentWindow.document;
  doc.open();
  doc.write(html);
  doc.close();
};

// Backwards-compat: keep the old portal-based API working so existing callers
// don't break. New code should call printBaggageTagInPopup directly.
export const triggerPrint = () => {
  const c = document.querySelector('.baggage-tag-print');
  if (!c) return window.print();
  const imgs = Array.from(c.querySelectorAll('img'));
  const pending = imgs.filter((i) => !i.complete || i.naturalWidth === 0);
  if (pending.length === 0) return window.print();
  let remaining = pending.length;
  const done = () => { remaining -= 1; if (remaining <= 0) window.print(); };
  pending.forEach((i) => {
    i.addEventListener('load', done, { once: true });
    i.addEventListener('error', done, { once: true });
  });
  setTimeout(() => window.print(), 1200);
};

export const BaggageTagPrint = ({ booking, customer }) => {
  if (!booking) return null;
  const bags = booking.luggage_details?.bags || [];
  const customerName = customer?.full_name || customer?.name || booking.customer_name || '—';
  const aadhaar = customer?.aadhaar_number || customer?.aadhaar_or_phone || booking.aadhaar_or_phone || '';
  const mobile = customer?.mobile_number || customer?.aadhaar_or_phone || booking.customer_phone || aadhaar;

  const content = (
    <div className="baggage-tag-print" data-testid="baggage-tag-printable">
      {bags.length === 0 ? (
        <LegacyTag booking={booking} customerName={customerName} mobile={mobile} bag={{}} bagIndex={1} totalBags={1} />
      ) : (
        bags.map((bag, idx) => (
          <LegacyTag key={idx} booking={booking} customerName={customerName} mobile={mobile} bag={bag} bagIndex={idx + 1} totalBags={bags.length} />
        ))
      )}
    </div>
  );
  if (typeof document === 'undefined') return content;
  return createPortal(content, document.body);
};

// Legacy portal-based tag (kept for backwards compat — interactive Barcode component).
const LegacyTag = ({ booking, customerName, mobile, bag, bagIndex, totalBags }) => {
  const tagValue = totalBags > 1 ? `${booking.baggage_tag}-${bagIndex}` : booking.baggage_tag;
  const trackUrl = `${window.location.origin}/track?tag=${booking.baggage_tag}`;
  return (
    <div className="bag-tag-page">
      <div className="bag-tag-card">
        <div className="tag-header">
          <div className="tag-brand-row">
            <img src="/lfj-logo.png" alt="" className="tag-logo" />
            <div className="tag-brand">LUGGAGEFREEJOURNEY</div>
          </div>
          <div className="tag-sub">OFFICIAL BAGGAGE TAG · BAG {bagIndex} OF {totalBags}</div>
        </div>
        <div className="tag-route">
          <div className="tag-route-side">
            <div className="tag-route-label">FROM</div>
            <div className="tag-route-city">{(booking.pickup_location || '').toUpperCase()}</div>
            <div className="tag-route-addr">{booking.pickup_address || ''}</div>
          </div>
          <div className="tag-arrow">→</div>
          <div className="tag-route-side">
            <div className="tag-route-label">TO</div>
            <div className="tag-route-city">{(booking.delivery_location || '').toUpperCase()}</div>
            <div className="tag-route-addr">{booking.delivery_address || ''}</div>
          </div>
        </div>
        <div className="tag-customer">
          <div><div className="tag-mini-label">PASSENGER</div><div className="tag-mini-value">{customerName.toUpperCase()}</div></div>
          <div><div className="tag-mini-label">MOBILE</div><div className="tag-mini-value">{maskMobile(mobile)}</div></div>
        </div>
        <div className="tag-bag-details">
          <div className="tag-bag-grid">
            <div><div className="tag-mini-label">TYPE</div><div className="tag-mini-value">{(bag.bag_type || 'BAG').toUpperCase()}</div></div>
            <div><div className="tag-mini-label">COLOR</div><div className="tag-mini-value">{(bag.color || '—').toUpperCase()}</div></div>
            <div><div className="tag-mini-label">COMPANY/BRAND</div><div className="tag-mini-value">{(bag.brand || '—').toUpperCase()}</div></div>
            <div><div className="tag-mini-label">WEIGHT</div><div className="tag-mini-value">{bag.weight ? `${bag.weight} KG` : '—'}</div></div>
          </div>
        </div>
        <div className="tag-codes">
          <div className="tag-barcode">
            <Barcode value={tagValue} />
            <div className="tag-number">{tagValue}</div>
          </div>
          <div className="tag-qr">
            <QRCodeSVG value={trackUrl} size={130} level="M" includeMargin={false} />
            <div className="tag-qr-label">SCAN TO TRACK</div>
          </div>
        </div>
        <div className="tag-footer">IF FOUND, CONTACT LUGGAGEFREEJOURNEY · KEEP THIS TAG SAFE</div>
      </div>
    </div>
  );
};

export default BaggageTagPrint;
